import React, { useState } from "react";
import { BarChart, XAxis, YAxis, Tooltip, Bar, CartesianGrid } from "recharts";
import ReportPageShell from "../components/ReportPageShell";
import MetricGrid from "../components/MetricGrid";
import StatCard from "../components/StatCard";
import { useReportQuery } from "../shared/useReportQuery";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { centsToUsd, formatDay, formatMetric, METRIC_LABELS } from "../shared/format";
import { MetricKey } from "../api/types";

const SUMMARY_METRICS: MetricKey[] = ["Gross", "OrderCount"];

const TOOLTIP =
  "Daily sales trend from the NEW unified reporting engine. Days follow the engine's " +
  "configured business-day boundary (shown above the report). Gross = item subtotal + tax only.";

// Parallel to the OLD financial chart's date axis. SalesByDay is a TimeSeries report
// (one row per day, ordered) carrying OrderCount + Gross.
export default function SalesByDayReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const state = useReportQuery(toQueryParams("SalesByDay", filters));

  return (
    <ReportPageShell
      title="Sales by Day"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
    >
      {(data) => {
        // Chart Gross over time (cents -> dollars), in chronological order.
        const chartData = data.rows.map((r) => ({
          day: r.label,
          gross: (r.values.Gross ?? 0) / 100,
        }));

        return (
          <>
            <div className="mb-3 grid grid-cols-2 gap-3">
              {SUMMARY_METRICS.filter((m) => data.metrics.includes(m)).map((m) => (
                <StatCard
                  key={m}
                  label={METRIC_LABELS[m]}
                  value={formatMetric(m, data.totals[m])}
                  emphasis={m === "Gross"}
                />
              ))}
            </div>

            <div className="mb-3 overflow-x-auto rounded-md bg-white p-2">
              <BarChart
                width={Math.max(720, chartData.length * 48)}
                height={320}
                data={chartData}
                margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatDay}
                  tick={{ fontSize: "0.75rem", fontWeight: 600, fill: "#4a5568" }}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={70}
                />
                <YAxis
                  width={90}
                  tickFormatter={(v: number) => centsToUsd(v * 100)}
                  tick={{ fontSize: "0.75rem", fontWeight: 600, fill: "#4a5568" }}
                />
                <Tooltip
                  formatter={(v: number) => centsToUsd(v * 100)}
                  labelFormatter={formatDay}
                />
                <Bar dataKey="gross" fill="#B71C1C" name="Gross Sales" />
              </BarChart>
            </div>

            <MetricGrid
              data={data}
              keyColumnHeader="Date"
              formatLabel={formatDay}
              exportName="SalesByDay"
            />
          </>
        );
      }}
    </ReportPageShell>
  );
}
